import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  IActivo,
  ICliente,
  ICreateRecorrido,
  IFilter,
  IGeoJSONLineString,
  IGrupo,
  IQueryParam,
  IQueryTripero,
  IRecorrido,
  IReporteGenerico,
  IUpdateRecorrido,
  Trip,
} from 'modelos/src';
import { firstValueFrom, Subscription } from 'rxjs';
import { RecorridosService } from '../../../../../auxiliares/servicios/http/recorridos.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { MatDialogRef } from '@angular/material/dialog';
import { CrearEditarAgrupacionesComponent } from '../../../../standalone/grupos/crear-editar-grupos/crear-editar-grupos.component';
import { ParamsService } from '../../../../../auxiliares/servicios/params.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { LoginService } from '../../../../login/login.service';
import { DateRange } from '../../../../../auxiliares/date-range-selector/date-range-selector.component';
import { TriperoService } from '../../../../../auxiliares/servicios/http/tripero.service';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';

@Component({
  selector: 'app-crear-editar-recorrido-vehiculos',
  templateUrl: './crear-editar-recorrido.component.html',
  styleUrl: './crear-editar-recorrido.component.scss',
  standalone: false,
})
export class CrearEditarRecorridoVehiculosComponent
  implements OnInit, OnDestroy
{
  dialogRef = inject<MatDialogRef<CrearEditarRecorridoVehiculosComponent>>(
    MatDialogRef,
    { optional: true },
  );
  helper = inject(HelperService);
  private service = inject(RecorridosService);
  private lista2 = inject(Lista2Service);
  private dialogService = inject(DialogService);
  private formularios = inject(FormularioDialogService);
  private paramsService = inject(ParamsService);
  private triperoService = inject(TriperoService);

  public loading = false;
  public title = '';
  public form?: FormGroup;
  public id?: string | null;

  public recorrido?: IRecorrido;

  public grupos?: IGrupo[];
  public grupos$?: Subscription;

  public clientePropio: ICliente = LoginService.getClientePropio();

  public fechaActual = new Date();
  // Importar recorrido desde vehículo
  public vehiculos: IActivo[] = [];
  public vehiculo?: IActivo;
  public vehiculos$?: Subscription;
  public range?: DateRange = {};
  public buscando = false;
  public loadingReportes = false;

  // Ajuste de la traza a las calles
  public ajustando = false;
  /** Traza previa al último ajuste, para poder deshacerlo. */
  private trazaAntesDelAjuste?: [number, number][];
  public interaccionesBusqueda: Trip[] = [];
  public interaccionesBusquedaSelected?: Trip;

  get geojson() {
    return this.form?.get('geojson') as FormGroup;
  }

  get cumplimientoForm() {
    return this.form?.get('cumplimiento') as FormGroup;
  }

  get duracionControl() {
    return this.form?.get('duracion') as FormControl;
  }

  get mideCumplimiento(): boolean {
    return !!this.cumplimientoForm?.get('activo')?.value;
  }

  /**
   * Preproceso de la traza (largo, buckets), calculado por el backend al
   * guardar. En un recorrido nuevo todavía no existe.
   */
  get geometria() {
    return this.recorrido?.geometria;
  }

  /**
   * El backend detectó que la traza vuelve al punto de partida, así que
   * probablemente sea cíclica y el porcentaje deba medirse por vuelta.
   */
  get sugiereCiclico(): boolean {
    return (
      !!this.geometria?.cerrado &&
      !this.cumplimientoForm?.get('esCiclico')?.value
    );
  }

  get canBuscar(): boolean {
    return !!this.vehiculo && !!this.range && !!this.range.start;
  }

  public async listarFlotas() {
    const filter: IFilter<IGrupo> = { categoria: 'Flota' };
    if (this.recorrido) {
      filter.idCliente = this.recorrido?.idCliente;
    }
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: 'nombre',
      select: 'nombre',
      limit: 0,
      includeChildren: true,
    };
    this.grupos$?.unsubscribe();
    this.grupos$ = this.lista2.grupos(query, 30).subscribe((data) => {
      this.grupos = data.datos;
    });
  }

  public async listarVehiculos() {
    const query: IQueryParam = {
      sort: 'nombre',
      page: 0,
      includeChildren: true,
      childrenLevel: 0,
    };
    this.vehiculos$?.unsubscribe();
    this.vehiculos$ = this.lista2.vehiculos(query, 30).subscribe((data) => {
      this.vehiculos = data.datos;
    });
  }

  private createForm() {
    const cumplimiento = this.recorrido?.cumplimiento;
    this.form = new FormGroup({
      // Select
      idGrupo: new FormControl(this.recorrido?.idGrupo),
      categoria: new FormControl('Vehiculo'),
      // Inputs
      nombre: new FormControl(this.recorrido?.nombre, Validators.required),
      color: new FormControl(this.recorrido?.color),
      // Duración esperada en minutos, usada por el cumplimiento por tiempo.
      duracion: new FormControl(this.recorrido?.duracion, [
        Validators.min(1),
        Validators.max(1440),
      ]),
      geojson: new FormGroup({
        type: new FormControl(this.recorrido?.geojson?.type ?? 'LineString'),
        coordinates: new FormControl(this.recorrido?.geojson?.coordinates),
      }),
      // Medición de cumplimiento (real vs deseado)
      cumplimiento: new FormGroup({
        activo: new FormControl(cumplimiento?.activo ?? false),
        porCobertura: new FormControl(cumplimiento?.porCobertura ?? true),
        porTiempo: new FormControl(cumplimiento?.porTiempo ?? false),
        // Los rangos evitan valores que no tienen sentido físico: una tolerancia
        // de kilómetros daría por cubierta media ciudad.
        toleranciaMetros: new FormControl(
          cumplimiento?.toleranciaMetros ?? 40,
          [Validators.min(5), Validators.max(500)],
        ),
        minimoPorcentaje: new FormControl(
          cumplimiento?.minimoPorcentaje ?? 100,
          [Validators.min(1), Validators.max(100)],
        ),
        toleranciaTiempoPorcentaje: new FormControl(
          cumplimiento?.toleranciaTiempoPorcentaje ?? 20,
          [Validators.min(0), Validators.max(200)],
        ),
        esCiclico: new FormControl(
          cumplimiento?.esCiclico ??
            this.recorrido?.geometria?.cerrado ??
            false,
        ),
        respetarOrden: new FormControl(cumplimiento?.respetarOrden ?? true),
        inicioEstricto: new FormControl(cumplimiento?.inicioEstricto ?? true),
        timeoutMinutos: new FormControl(cumplimiento?.timeoutMinutos ?? 30, [
          Validators.min(1),
          Validators.max(1440),
        ]),
      }),
    });
  }

  public async onSubmit() {
    const res = await this.dialogService.confirm(
      `${this.recorrido ? 'Editar' : 'Crear'} Recorrido`,
      `¿Querés ${this.recorrido ? 'editar' : 'crear'} el recorrido?`,
    );
    if (!res) return;
    this.loading = true;
    try {
      let res;
      if (this.recorrido) {
        // Update
        const data = this.getData();
        if (!data) return;
        res = await this.service.update(this.recorrido._id, data);
        this.helper.notifSuccess('Recorrido actualizado');
      } else {
        // Create
        const data = this.getData();
        if (!data) return;
        res = await this.service.create(data);
        this.helper.notifSuccess('Recorrido creado');
      }
      this.volver(res);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  private getData() {
    const data: ICreateRecorrido | IUpdateRecorrido = this.form?.value;

    const geojson = data.geojson as IGeoJSONLineString | undefined;
    if (!geojson?.coordinates?.length) {
      delete data.geojson;
    }

    return data;
  }

  public volver(res?: any) {
    if (this.dialogRef) {
      this.dialogRef.close(res);
    } else {
      HelperService.volver();
    }
  }

  public crearGrupo() {
    const dialog = this.formularios.abrir(CrearEditarAgrupacionesComponent, {
      tamano: 'mediano',
      data: { categoria: 'Flota' },
    });
    dialog.afterClosed().subscribe((res) => {
      if (res) {
        this.form.patchValue({ idGrupo: res });
      }
    });
  }

  // LÓGICA DE IMPORTAR RECORRIDO DESDE VEHÍCULO

  public buscar() {
    if (!this.canBuscar) {
      return;
    }
    this.buscando = true;
    this.listarViajes();
  }

  public async listarViajes() {
    if (!this.range?.start || !this.vehiculo) {
      this.interaccionesBusqueda = [];
      this.buscando = false;
      return;
    }

    try {
      const date = new Date(this.range.start);
      const start = new Date(
        date.getTime() - 12 * 60 * 60 * 1000,
      ).toISOString();
      const end = new Date(date.getTime() + 12 * 60 * 60 * 1000).toISOString();
      const query: IQueryTripero = {
        idVehiculo: this.vehiculo._id!,
        from: start,
        to: end,
      };
      const data = await this.triperoService.getTrips(query);
      if (data) {
        // ordenar por startTime descendente (más reciente primero)
        this.interaccionesBusqueda = data.sort((a, b) => {
          return a.startTime! < b.startTime! ? 1 : -1;
        });
      } else {
        this.interaccionesBusqueda = [];
      }
    } catch (err) {
      console.error('Error listando viajes', err);
      this.interaccionesBusqueda = [];
    } finally {
      this.buscando = false;
    }
  }

  public async onRecorridoSelected(trip: Trip, expanded: boolean) {
    if (expanded) {
      this.interaccionesBusquedaSelected = trip;
      await this.listarReportes();
    }
  }

  public async listarReportes() {
    if (!this.vehiculo || !this.interaccionesBusquedaSelected) return;

    const filter: IFilter<IReporteGenerico> = {
      tipoEntidad: 'Vehiculo',
      idsAsignados: this.vehiculo._id!,
      'valores.timestamp': {
        $gte: this.interaccionesBusquedaSelected.startTime,
        $lte: this.interaccionesBusquedaSelected.endTime,
      },
    } as any;
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: '-valores.timestamp',
      includeChildren: true,
    };

    this.loadingReportes = true;
    const coordsSnapped = await firstValueFrom(
      this.lista2.snappeadosGenericos(query),
    );

    this.loadingReportes = false;
    this.cargarRecorridoImportado(coordsSnapped);
  }

  private cargarRecorridoImportado(coordenadas: [number, number][]) {
    if (!coordenadas || coordenadas.length === 0) {
      this.helper.notifError('No se encontraron coordenadas para este viaje');
      return;
    }

    // El mapa escucha valueChanges de coordinates y redibuja con fitBounds
    this.geojson.patchValue({
      type: 'LineString',
      coordinates: coordenadas,
    });

    this.helper.notifSuccess(
      `Recorrido importado con ${coordenadas.length} puntos`,
    );
  }

  public resetearImportacion() {
    this.vehiculo = undefined;
    this.range = {};
    this.interaccionesBusqueda = [];
    this.interaccionesBusquedaSelected = undefined;
    this.buscando = false;
    this.loadingReportes = false;
  }

  // AJUSTE DE LA TRAZA A LAS CALLES

  /** Coordenadas actuales del dibujo. */
  private get coordenadasTraza(): [number, number][] {
    return (this.geojson?.get('coordinates')?.value ?? []) as [
      number,
      number,
    ][];
  }

  get puedeAjustarACalles(): boolean {
    return !this.ajustando && this.coordenadasTraza.length >= 2;
  }

  get puedeDeshacerAjuste(): boolean {
    return !this.ajustando && !!this.trazaAntesDelAjuste;
  }

  /**
   * Reemplaza la traza dibujada por la ruta que sigue las calles entre sus
   * vértices.
   *
   * Importa para el cumplimiento: una traza de pocos puntos sobre un camino curvo
   * deja cuerdas rectas que se alejan cientos de metros del asfalto, y el
   * porcentaje se mide contra esas cuerdas, no contra el camino.
   */
  public async ajustarACalles() {
    const coordenadas = this.coordenadasTraza;
    if (coordenadas.length < 2) {
      this.helper.notifError('Dibujá la traza antes de ajustarla');
      return;
    }

    this.ajustando = true;
    try {
      const ajustada = await this.service.ajustarTrazaACalles(coordenadas);
      if (!ajustada?.length) {
        this.helper.notifError(
          'No se pudo ajustar la traza a las calles: no se encontró una ruta',
        );
        return;
      }
      // Se guarda la anterior para poder volver: OSRM elige la mejor ruta entre
      // los vértices y puede no ser la que el operador quiso dibujar.
      this.trazaAntesDelAjuste = coordenadas;
      this.geojson.patchValue({ type: 'LineString', coordinates: ajustada });
      this.helper.notifSuccess(
        `Traza ajustada a las calles: ${coordenadas.length} → ${ajustada.length} puntos`,
      );
    } catch (error) {
      console.error('Error ajustando la traza:', error);
      this.helper.notifError(error);
    } finally {
      this.ajustando = false;
    }
  }

  /** Vuelve a la traza que había antes del último ajuste. */
  public deshacerAjuste() {
    if (!this.trazaAntesDelAjuste) return;
    this.geojson.patchValue({
      type: 'LineString',
      coordinates: this.trazaAntesDelAjuste,
    });
    this.trazaAntesDelAjuste = undefined;
    this.helper.notifSuccess('Se restauró la traza anterior');
  }

  /// HOOKS

  async ngOnInit() {
    // getParams() es null si nunca se seteó (ej: navegación directa por URL)
    this.recorrido = this.paramsService.getParams()?.['recorrido'];
    if (this.recorrido) {
      this.title = 'Editar Recorrido';
    } else {
      this.title = 'Crear Recorrido';
    }

    this.createForm();
    await Promise.all([this.listarFlotas(), this.listarVehiculos()]);
  }

  ngOnDestroy(): void {
    this.grupos$?.unsubscribe();
    this.vehiculos$?.unsubscribe();
  }
}
